import { Box } from '@chakra-ui/react'
import type { MouseEventHandler } from 'react'
import { useEffect, useRef, useState } from 'react'

import FloorUnselected from '../assets/images/floor-unselected.svg?react'
import RoofSelected from '../assets/images/roof-selected.svg?react'

import RoofUnselected from '../assets/images/roof-unselected.svg?react'
import { Add, CheckCircle } from '../icons'

const Roof = ({
  selected = false,
  onClick,
}: {
  selected?: boolean
  onClick: MouseEventHandler<HTMLDivElement>
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        width: '15rem',
        height: '2.75rem',
        gap: 2,
        pb: 1.5,
        placeContent: 'center',
        placeItems: 'end',
        bgSize: 'contain',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <Box position='absolute'>
        {selected ? <RoofSelected /> : <RoofUnselected />}
      </Box>
      {!selected && <Add />}
      {selected && <CheckCircle />}
      podkroví
    </Box>
  )
}

const Floor = ({
  children,
  selected = false,
  number,
  onClick,
}: {
  children?: React.ReactNode
  selected?: boolean
  number?: number
  onClick: Function
}) => {
  return (<Box position='relative' bgColor='white'>
    <Box position='absolute'>
      {!selected && <FloorUnselected />}
    </Box>
    <Box
      sx={{
        display: 'flex',
        width: '15rem',
        height: '2.75rem',
        gap: 2,
        placeContent: 'center',
        placeItems: 'center',
        borderRadius: 'md',
        border: selected ? '1px solid' : 'none',
        borderColor: selected ? 'green.500' : 'transparent',
        bgSize: 'contain',
        cursor: 'pointer',
        zIndex: 10,
        position: 'relative',
      }}
      onClick={() => onClick(selected)}
    >
      {!selected && <Add />}
      {selected && <CheckCircle />}
      {!children && (number == 0 ? 'přízemí' : `${number}. patro`)}
      {children && children}
    </Box>
  </Box>
  )
}

export const HouseFloorSelector = ({
  floors = 1,
  basement = false,
  roof,
  showRoof,
  onFloorsChange,
  onRoofChange,
  onBasementChange,
}: {
  floors: number
  basement: boolean
  roof: boolean
  showRoof: boolean
  onFloorsChange: Function
  onRoofChange: Function
  onBasementChange: Function
}) => {
  if (isNaN(floors) || floors < 0)
    floors = 0
  const [roofSelected, setRoofSelected] = useState(roof)
  const [floorsSelected, setFloorsSelected] = useState<number>(floors)
  const [basementSelected, setBasementSelected] = useState(basement)
  const [, setForceUpdate] = useState(Date.now())
  const floorsArray = useRef(Array.from(Array(floorsSelected < 0 ? 0 : floorsSelected), (_, i) => i))

  const handleRoofClick = () => {
    setRoofSelected(!roofSelected)
    onRoofChange(!roofSelected)
  }

  const handleFloorClick = (isSelected: boolean) => {
    let newFloorsSelected

    if (isSelected) {
      newFloorsSelected = floorsSelected - 1
    } else {
      newFloorsSelected = floorsSelected + 1
    }

    setFloorsSelected(newFloorsSelected)
    onFloorsChange(newFloorsSelected)
  }

  const handleBasementClick = (selected: boolean) => {
    onBasementChange(!selected)
    setBasementSelected(!selected)
  }

  useEffect(() => {
    floorsArray.current = Array.from(Array(floorsSelected), (_, i) => i)
    setForceUpdate(Date.now())
  }, [floorsSelected])

  useEffect(() => {
    setFloorsSelected(floors)
    setForceUpdate(Date.now())
  }, [floors])

  useEffect(() => {
    setBasementSelected(basement)
    setForceUpdate(Date.now())
  }, [basement])

  useEffect(() => {
    setRoofSelected(roof)
    setForceUpdate(Date.now())
  }, [roof])

  return (
    <Box sx={{ display: 'grid', gap: 2, placeItems: 'center' }}>
      {showRoof && <Roof selected={roofSelected} onClick={handleRoofClick} />}

      <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column-reverse' }}>
        {floorsArray.current.length > 0 &&
          floorsArray.current.map((floor) => {
            return <Floor key={floor} number={floor} selected onClick={handleFloorClick} />
          })}

        <Floor key={floorsSelected} number={floorsSelected} onClick={handleFloorClick} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          gap: 2,
          pt: 2,
          pb: 4,
          px: 8,
          bg: 'gray.50',
          borderTop: '1px solid',
          borderTopColor: 'gray.300',
        }}
      >
        <Floor selected={basementSelected} onClick={handleBasementClick}>
          sklep
        </Floor>
      </Box>
    </Box>
  )
}